import { roundMoney } from "./local-pricing-snapshot";
import type { CachedCouponRuleRecord } from "./types";

export type ServerCouponLike = {
  id: string;
  code: string;
  discountType: "PERCENT" | "AMOUNT";
  discountValue: number | string;
  maxUses?: number | null;
  maxUsesPerCustomer?: number | null;
  customerId?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  active: boolean;
  metadata?: unknown;
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

export function extractCouponConstraints(
  metadata?: unknown,
): Pick<CachedCouponRuleRecord, "minSpend" | "allowedOrderTypes"> {
  const meta =
    metadata && typeof metadata === "object"
      ? (metadata as Record<string, unknown>)
      : null;
  const minSpend = typeof meta?.minSpend === "number" ? meta.minSpend : undefined;
  const orderTypes = meta?.orderTypes;
  const allowedOrderTypes = Array.isArray(orderTypes)
    ? orderTypes.filter(
        (value): value is "DINE_IN" | "TAKEAWAY" | "DELIVERY" =>
          value === "DINE_IN" || value === "TAKEAWAY" || value === "DELIVERY",
      )
    : undefined;

  return {
    minSpend,
    allowedOrderTypes:
      allowedOrderTypes && allowedOrderTypes.length > 0
        ? allowedOrderTypes
        : undefined,
  };
}

export function toCachedCouponRuleRecord(
  coupon: ServerCouponLike,
  cachedAt = new Date().toISOString(),
): CachedCouponRuleRecord {
  const constraints = extractCouponConstraints(coupon.metadata);

  return {
    id: coupon.id,
    code: coupon.code.trim().toUpperCase(),
    discountType: coupon.discountType,
    discountValue: Number(coupon.discountValue),
    maxUses: coupon.maxUses,
    maxUsesPerCustomer: coupon.maxUsesPerCustomer,
    customerId: coupon.customerId,
    validFrom: coupon.validFrom,
    validTo: coupon.validTo,
    active: coupon.active,
    metadata:
      coupon.metadata && typeof coupon.metadata === "object"
        ? (coupon.metadata as Record<string, unknown>)
        : null,
    minSpend: constraints.minSpend,
    allowedOrderTypes: constraints.allowedOrderTypes,
    cachedAt,
    lastUsedAt: cachedAt,
  };
}

export function calculateCouponDiscountFromSnapshot(
  rule: Pick<CachedCouponRuleRecord, "discountType" | "discountValue">,
  baseAmountInclTax: number,
): number {
  if (rule.discountType === "PERCENT") {
    return roundMoney((baseAmountInclTax * rule.discountValue) / 100);
  }
  if (rule.discountType === "AMOUNT") {
    return roundMoney(rule.discountValue);
  }
  return 0;
}

export type ValidateCouponFromCacheInput = {
  rule: CachedCouponRuleRecord;
  customerId?: string;
  baseAmount: number;
  orderType?: "DINE_IN" | "TAKEAWAY" | "DELIVERY";
  capturedAt?: string;
};

export type ValidateCouponFromCacheResult = {
  valid: boolean;
  coupon?: CachedCouponRuleRecord;
  calculatedDiscount?: number;
  error?: string;
  needsServerConfirmation: boolean;
};

export function validateCouponFromCache(
  input: ValidateCouponFromCacheInput,
): ValidateCouponFromCacheResult {
  const rule = input.rule;
  const now = input.capturedAt ?? new Date().toISOString();

  if (!rule.active) {
    return {
      valid: false,
      error: "Coupon is not active",
      needsServerConfirmation: true,
    };
  }

  if (rule.validFrom && rule.validFrom > now) {
    return {
      valid: false,
      error: "Coupon is not yet valid",
      needsServerConfirmation: true,
    };
  }

  if (rule.validTo && rule.validTo < now) {
    return {
      valid: false,
      error: "Coupon has expired",
      needsServerConfirmation: true,
    };
  }

  if (rule.customerId && rule.customerId !== input.customerId) {
    return {
      valid: false,
      error: "Coupon is not valid for this customer",
      needsServerConfirmation: true,
    };
  }

  if (
    input.orderType &&
    rule.allowedOrderTypes?.length &&
    !rule.allowedOrderTypes.includes(input.orderType)
  ) {
    return {
      valid: false,
      error: "Coupon is not valid for this order type",
      needsServerConfirmation: true,
    };
  }

  if (rule.minSpend != null && input.baseAmount < rule.minSpend) {
    return {
      valid: false,
      error: "Minimum spend not met",
      needsServerConfirmation: true,
    };
  }

  const calculatedDiscount = calculateCouponDiscountFromSnapshot(
    rule,
    input.baseAmount,
  );

  return {
    valid: true,
    coupon: rule,
    calculatedDiscount,
    needsServerConfirmation: true,
  };
}
